/**
 * Aspect — Tour camera (room-to-room glides + auto-tour)
 * --------------------------------------------------------------
 * Smoothly flies the first-person camera to any named room (click a room in
 * the tour panel / minimap) and runs an auto-tour that visits every room in
 * sequence, pausing on each with its label + area.
 *
 * Cooperates with FirstPersonControls: walk input is suspended while a glide
 * is in flight and the final pose is handed back via fp.adoptPose(), so the
 * player can continue walking seamlessly from where the tour left them.
 * If the overhead scene view is active it is dismissed first.
 *
 * Fires (UI listens; this module never touches the DOM):
 *   "tour:rooms-changed"  { rooms: [{ index, name, type, area }] }
 *   "tour:state"          { active, auto, roomIndex, label, area }
 */

import * as pc from "playcanvas";

const EYE_HEIGHT = 1.6;
const GLIDE_SPEED = 3.6; // m/s equivalent for duration scaling
const MIN_DURATION = 0.7;
const MAX_DURATION = 2.6;
const AUTO_PAUSE = 3.2; // seconds spent looking around in each room
const AUTO_PAN_DEG = 48; // gentle look-around sweep while paused

const smoothstep = (t) => t * t * (3 - 2 * t);
const shortestDeltaDeg = (from, to) => {
  let d = (to - from) % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
};

/** World-space centre of a named-room entry (polygon average, else rect centre). */
export function roomCenterXZ(entry) {
  const poly = Array.isArray(entry?.polygon) && entry.polygon.length >= 3 ? entry.polygon : null;
  if (poly) {
    let sx = 0;
    let sz = 0;
    for (const [x, z] of poly) {
      sx += Number(x) || 0;
      sz += Number(z) || 0;
    }
    return [sx / poly.length, sz / poly.length];
  }
  return [
    (Number(entry?.x) || 0) + (Number(entry?.width) || 0) / 2,
    (Number(entry?.z) || 0) + (Number(entry?.depth) || 0) / 2,
  ];
}

/** Display label + area for a named-room entry (mirrors the minimap wording). */
export function roomDisplay(entry, index) {
  const name = String(entry?.name ?? entry?.label ?? `Room ${index + 1}`).trim() || `Room ${index + 1}`;
  const reported = Number(entry?.reportedArea);
  const w = Number(entry?.width) || 0;
  const d = Number(entry?.depth) || 0;
  const area = Number.isFinite(reported) && reported > 0 ? reported : w > 0 && d > 0 ? w * d : null;
  return { name, area: area != null ? Math.round(area * 10) / 10 : null };
}

export class TourCamera {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {{ fp?: object, sceneView?: object }} [opts]
   */
  constructor(app, camera, scene, { fp = null, sceneView = null } = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.fp = fp;
    this.sceneView = sceneView;

    this._glide = null; // { fromPos, toPos, fromYaw, toYaw, fromPitch, toPitch, t, duration }
    this._auto = null; // { order: number[], i, pauseLeft, baseYaw }
    this._pending = null; // room queued while the overhead camera transitions out
    this._activeIndex = -1;

    this._onUpdate = (dt) => this._update(dt);
    app.on("update", this._onUpdate);
    this._onRoom = () => {
      this.stop();
      this._announceRooms();
    };
    app.on("scene:room-loaded", this._onRoom);
    // Any user takeover (pointer lock, overhead toggle) cancels the tour.
    this._onLock = ({ locked }) => {
      if (locked) this.stop();
    };
    app.on("fp:lock-changed", this._onLock);
    this._onMode = ({ mode }) => {
      if (mode === "overhead") this.stop();
    };
    app.on("cam:mode-changed", this._onMode);
    // Movement keys / Esc hand control back to the user mid-tour.
    this._onKey = (e) => {
      if (!this.active) return;
      const k = e.key;
      if (
        k === pc.KEY_ESCAPE ||
        k === pc.KEY_W ||
        k === pc.KEY_A ||
        k === pc.KEY_S ||
        k === pc.KEY_D ||
        k === pc.KEY_UP ||
        k === pc.KEY_DOWN ||
        k === pc.KEY_LEFT ||
        k === pc.KEY_RIGHT
      ) {
        this.stop();
      }
    };
    app.keyboard?.on(pc.EVENT_KEYDOWN, this._onKey);
  }

  /** Named rooms of the active floor, with display metadata. */
  rooms() {
    const list = Array.isArray(this.scene.room?.rooms) ? this.scene.room.rooms : [];
    return list
      .map((entry, index) => {
        const { name, area } = roomDisplay(entry, index);
        const [cx, cz] = roomCenterXZ(entry);
        return { index, name, type: entry?.type ?? null, area, cx, cz, entry };
      })
      .filter((r) => Number.isFinite(r.cx) && Number.isFinite(r.cz));
  }

  _announceRooms() {
    this.app.fire("tour:rooms-changed", {
      rooms: this.rooms().map(({ index, name, type, area }) => ({ index, name, type, area })),
    });
  }

  get active() {
    return Boolean(this._glide || this._auto || this._pending);
  }

  /** Glide to the room at `index` in the rooms() list. */
  goToRoom(index) {
    const room = this.rooms().find((r) => r.index === index);
    if (!room) return false;
    this._auto = null;
    this._beginGlide(room);
    return true;
  }

  /** Visit every room in sequence, pausing on each. */
  startTour() {
    const rooms = this.rooms();
    if (!rooms.length) return false;
    // Nearest room first, then walk the list in order from there.
    const pos = this.camera.getPosition();
    let startI = 0;
    let bestD = Infinity;
    rooms.forEach((r, i) => {
      const d = Math.hypot(r.cx - pos.x, r.cz - pos.z);
      if (d < bestD) {
        bestD = d;
        startI = i;
      }
    });
    const order = rooms.map((_, i) => rooms[(startI + i) % rooms.length].index);
    this._auto = { order, i: 0, pauseLeft: 0, baseYaw: 0, pausing: false };
    this._beginGlide(rooms.find((r) => r.index === order[0]));
    return true;
  }

  stop() {
    if (!this.active) return;
    const wasGliding = Boolean(this._glide);
    this._glide = null;
    this._auto = null;
    this._pending = null;
    this._activeIndex = -1;
    // Never resurrect walk controls while the overhead camera owns the view.
    if (wasGliding && !this.sceneView?.enabled && !this.sceneView?.isTransitioning) this._handBackToWalk();
    this.app.fire("tour:state", { active: false, auto: false, roomIndex: -1, label: null, area: null });
  }

  _beginGlide(room) {
    if (!room) return;
    // Dismiss the overhead view first and wait out its camera transition so the
    // two animations never fight over the camera pose.
    if (this.sceneView?.enabled || this.sceneView?.isTransitioning) {
      if (this.sceneView.enabled && !this.sceneView.isTransitioning) this.sceneView.disable();
      this._pending = room;
      return;
    }
    this._pending = null;
    this.fp?.releaseLock?.();
    this.fp?.setEnabled?.(false);

    const floorY = this.scene.room?.floorY ?? 0;
    const eyeY = floorY + EYE_HEIGHT;
    const from = this.camera.getPosition().clone();
    const to = new pc.Vec3(room.cx, eyeY, room.cz);

    const euler = this.camera.getLocalEulerAngles();
    const fromYaw = euler.y;
    const fromPitch = euler.x;
    // Arrive facing the direction of travel (looking into the room).
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const dist = Math.hypot(dx, dz);
    const toYaw = dist > 0.4 ? (Math.atan2(-dx, -dz) * 180) / Math.PI : fromYaw;
    const duration = Math.min(MAX_DURATION, Math.max(MIN_DURATION, dist / GLIDE_SPEED));

    this._activeIndex = room.index;
    this._glide = {
      fromPos: from,
      toPos: to,
      fromYaw,
      toYaw,
      yawDelta: shortestDeltaDeg(fromYaw, toYaw),
      fromPitch,
      toPitch: -6,
      t: 0,
      duration,
      label: room.name,
      area: room.area,
    };
    this.app.fire("tour:state", {
      active: true,
      auto: Boolean(this._auto),
      roomIndex: room.index,
      label: room.name,
      area: room.area,
    });
  }

  _update(dt) {
    if (this._pending && !this.sceneView?.isTransitioning && !this.sceneView?.enabled) {
      const room = this._pending;
      this._pending = null;
      this._beginGlide(room);
      return;
    }
    const g = this._glide;
    if (g) {
      g.t = Math.min(1, g.t + dt / g.duration);
      const k = smoothstep(g.t);
      const x = g.fromPos.x + (g.toPos.x - g.fromPos.x) * k;
      const y = g.fromPos.y + (g.toPos.y - g.fromPos.y) * k;
      const z = g.fromPos.z + (g.toPos.z - g.fromPos.z) * k;
      const yaw = g.fromYaw + g.yawDelta * k;
      const pitch = g.fromPitch + (g.toPitch - g.fromPitch) * k;
      this.camera.setLocalPosition(x, y, z);
      this.camera.setLocalEulerAngles(pitch, yaw, 0);
      this.app.renderNextFrame = true;
      if (g.t >= 1) {
        this._glide = null;
        if (this._auto) {
          this._auto.pausing = true;
          this._auto.pauseLeft = AUTO_PAUSE;
          this._auto.baseYaw = yaw;
        } else {
          this._finish(yaw, pitch);
        }
      }
      return;
    }

    const a = this._auto;
    if (a?.pausing) {
      a.pauseLeft -= dt;
      // Gentle look-around sweep while paused in the room.
      const p = 1 - Math.max(0, a.pauseLeft) / AUTO_PAUSE;
      const sweep = Math.sin(p * Math.PI * 2) * (AUTO_PAN_DEG / 2);
      this.camera.setLocalEulerAngles(-6, a.baseYaw + sweep, 0);
      this.app.renderNextFrame = true;
      if (a.pauseLeft <= 0) {
        a.pausing = false;
        a.i += 1;
        if (a.i >= a.order.length) {
          const e = this.camera.getLocalEulerAngles();
          this._auto = null;
          this._finish(e.y, e.x);
        } else {
          this._beginGlide(this.rooms().find((r) => r.index === a.order[a.i]));
        }
      }
    }
  }

  /** Hand the final pose to walk controls and announce the idle state. */
  _finish(yaw, pitch) {
    this._activeIndex = -1;
    this._handBackToWalk(yaw, pitch);
    this.app.fire("tour:state", { active: false, auto: false, roomIndex: -1, label: null, area: null });
  }

  _handBackToWalk(yaw = null, pitch = null) {
    this.fp?.setEnabled?.(true);
    const pos = this.camera.getPosition();
    const e = this.camera.getLocalEulerAngles();
    this.fp?.adoptPose?.(pos, yaw ?? e.y, pitch ?? e.x);
  }

  destroy() {
    this.app.off("update", this._onUpdate);
    this.app.off("scene:room-loaded", this._onRoom);
    this.app.off("fp:lock-changed", this._onLock);
    this.app.off("cam:mode-changed", this._onMode);
    this._glide = null;
    this._auto = null;
  }
}

export default TourCamera;
